import { cn } from '@/lib/utils';
import { ArrowRight, ArrowDown } from 'lucide-react';

interface LearningPathProps {
  section: any;
}

const LEVEL_STYLES: Record<string, string> = {
  beginner: 'bg-success-100 text-success-800 border-success-300',
  intermediate: 'bg-amber-100 text-amber-800 border-amber-300',
  advanced: 'bg-red-100 text-red-800 border-red-300',
};

export default function LearningPath({ section }: LearningPathProps) {
  const paths: any[] = section.content?.paths ?? [];
  const style: string = section.content?.style ?? 'linear';
  const headingId = `learning-path-heading-${section.id}`;

  if (paths.length === 0) return null;

  const sorted = [...paths].sort((a, b) => {
    const order = ['beginner', 'intermediate', 'advanced'];
    return order.indexOf(a.level) - order.indexOf(b.level);
  });

  return (
    <section aria-labelledby={headingId} className="py-16 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          {section.title && (
            <h2
              id={headingId}
              className="font-display text-3xl font-bold text-sage-900 sm:text-4xl"
            >
              {section.title}
            </h2>
          )}
          {section.subtitle && (
            <p className="mt-2 text-sage-600 max-w-xl mx-auto">
              {section.subtitle}
            </p>
          )}
        </div>

        {style === 'grid' ? (
          /* Grid layout */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {sorted.map((path: any, i: number) => (
              <div
                key={i}
                className="rounded-xl bg-surface p-6 shadow-sm border border-sage-100 flex flex-col"
              >
                <span
                  className={cn(
                    'inline-flex self-start rounded-full px-3 py-0.5 text-xs font-semibold border',
                    LEVEL_STYLES[path.level] ?? LEVEL_STYLES.beginner
                  )}
                >
                  {path.level}
                </span>
                <h3 className="mt-3 font-display text-lg font-semibold text-sage-900">
                  {path.title}
                </h3>
                {path.duration && (
                  <p className="mt-1 text-sm text-sage-500">{path.duration}</p>
                )}
                {path.description && (
                  <p className="mt-2 text-sm text-sage-600 flex-1">
                    {path.description}
                  </p>
                )}
              </div>
            ))}
          </div>
        ) : (
          /* Linear layout: horizontal on desktop, vertical on mobile */
          <div className="flex flex-col items-center gap-4 md:flex-row md:justify-center md:gap-0">
            {sorted.map((path: any, i: number) => (
              <div key={i} className="flex flex-col items-center md:flex-row md:items-center">
                {/* Path card */}
                <div className="rounded-xl bg-surface p-6 shadow-sm border border-sage-100 w-full max-w-xs text-center">
                  <span
                    className={cn(
                      'inline-flex rounded-full px-3 py-0.5 text-xs font-semibold border',
                      LEVEL_STYLES[path.level] ?? LEVEL_STYLES.beginner
                    )}
                  >
                    {path.level}
                  </span>
                  <h3 className="mt-3 font-display text-lg font-semibold text-sage-900">
                    {path.title}
                  </h3>
                  {path.duration && (
                    <p className="mt-1 text-sm text-sage-500">{path.duration}</p>
                  )}
                  {path.description && (
                    <p className="mt-2 text-sm text-sage-600">
                      {path.description}
                    </p>
                  )}
                </div>

                {/* Arrow connector */}
                {i < sorted.length - 1 && (
                  <>
                    <ArrowDown
                      className="h-6 w-6 text-sage-400 my-2 md:hidden"
                      aria-hidden="true"
                    />
                    <ArrowRight
                      className="hidden md:block h-6 w-6 text-sage-400 mx-4 shrink-0"
                      aria-hidden="true"
                    />
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
